import React, { useEffect } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import {
  Add,
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  Delete,
} from "@mui/icons-material";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import FileDropzone from "components/Shared/FileDropzone";

import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useVehicleDetailContext } from "contexts/VehicleDetailContext";

import { useAuth, useDOUploadFile } from "hooks";

import { FUEL_TYPES } from "data/static/staticOptions";
import { useLazyGetDDLMerchantListByTypesQuery } from "store/services/merchantApi";
import moment from "moment";
import { getDateTimeAuto } from "utils/dateFormats";
import { useLazyGetDDLVehicleListForVehicleFuelQuery } from "store/services/vehicleApi";

const INITIAL_STATE = {
  account: "",
  entity: "",
  vehicleId: "",
  merchant: "",
  odometer: "",
  fuel: [{ fuelType: "", price: "", quantity: "" }],
  attachments: [],
  isActive: true,
  fuelDate: getDateTimeAuto(),
};

const VehicleFuelForm = ({
  isBusy = false,
  vehicleFuelDetail = null,
  onSubmit = () => {},
}) => {
  const [showFuelDetails, setShowFuelDetails] = React.useState(true);
  const [selectedVehicleOdometer, setSelectedVehicleOdometer] =
    React.useState(0);

  const navigate = useNavigate();

  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const { handleUploadFiles } = useDOUploadFile();

  let vehicleDetail = null;
  try {
    const vehicleDetailContext = useVehicleDetailContext();
    vehicleDetail = vehicleDetailContext
      ? vehicleDetailContext.vehicleDetail
      : null;
  } catch (error) {
    console.warn(
      "VehicleDetailContext not provided, using dropdown for vehicle selection."
    );
  }

  // React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      fuelDate: getDateTimeAuto(),
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
      vehicleId: vehicleDetail
        ? {
            _id: vehicleDetail._id,
            vehicle_id: vehicleDetail.vehicle_id,
            registration_number: vehicleDetail.registration_number,
          }
        : "",
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchVehicleId = useWatch({ control, name: "vehicleId" });

  // RTK Query
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchVehicles, { data: vehicles, isLoading: loadingVehicles }] =
    useLazyGetDDLVehicleListForVehicleFuelQuery();
  const [fetchMerchants, { data: merchants, isLoading: loadingMerchants }] =
    useLazyGetDDLMerchantListByTypesQuery();

  // Field array for fuel
  const { fields, append, remove } = useFieldArray({
    control,
    name: "fuel",
  });

  // Update selected vehicle odometer when vehicle changes
  React.useEffect(() => {
    if (_watchVehicleId?._id && vehicles) {
      const selectedVehicle = vehicles.find(
        (v) => v._id === _watchVehicleId._id
      );
      if (selectedVehicle && selectedVehicle.odometer) {
        setSelectedVehicleOdometer(selectedVehicle.odometer);
      }
    } else if (vehicleDetail && vehicleDetail.odometer) {
      setSelectedVehicleOdometer(vehicleDetail.odometer);
    }
  }, [_watchVehicleId, vehicles, vehicleDetail]);

  useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id) {
      fetchMerchants({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        merchantType: "Fuel Station",
        active: "true",
      });
    }
  }, [_watchAccount?._id, _watchEntity?._id, fetchMerchants]);

  // Methods
  const handleOnVehicleFuelSubmit = async (data) => {
    const distance = Number(data.odometer) - selectedVehicleOdometer;

    const _vehicleFuelPayLoad = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      vehicleId: vehicleDetail?._id || data?.vehicleId?._id,
      merchant: data?.merchant?._id,
      distance: distance,
    };

    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([pass, fail], el) =>
        el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
      [[], []]
    );

    if (newAttachments?.length) {
      const { uploadedFiles } = await handleUploadFiles(newAttachments, {
        folderName: "VEHICLE_FUEL_DOCUMENT",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });
      _vehicleFuelPayLoad.attachments = [
        ...existingAttachments,
        ...uploadedFiles,
      ];
    } else {
      _vehicleFuelPayLoad.attachments = existingAttachments;
    }

    onSubmit(_vehicleFuelPayLoad);
  };

  React.useEffect(() => {
    if (!vehicleFuelDetail) return;
    reset({
      ...vehicleFuelDetail,
      fuel: vehicleFuelDetail.fuel.length
        ? vehicleFuelDetail.fuel
        : [{ fuelType: "", price: "", quantity: "" }],
      attachments: vehicleFuelDetail.attachments || [],
      vehicleId: vehicleDetail
        ? {
            _id: vehicleDetail._id,
            vehicle_id: vehicleDetail.vehicle_id,
            registration_number: vehicleDetail.registration_number,
          }
        : vehicleFuelDetail.vehicleId || "",
      ...(vehicleFuelDetail?.fuelDate && {
        fuelDate: moment(vehicleFuelDetail.fuelDate).format("YYYY-MM-DDTHH:mm"),
      }),
      merchant: vehicleFuelDetail.merchant || "",
    });
    setShowFuelDetails(vehicleFuelDetail.fuel.length > 0);
  }, [vehicleFuelDetail, reset, vehicleDetail]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  React.useEffect(() => {
    if (!_watchEntity?._id || vehicleDetail) return;
    fetchVehicles({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchVehicles, vehicleDetail]);

  React.useEffect(() => {
    if (vehicleDetail) {
      setValue("vehicleId", {
        _id: vehicleDetail._id,
        vehicle_id: vehicleDetail.vehicle_id,
        registration_number: vehicleDetail.registration_number,
      });
    }
  }, [vehicleDetail, setValue]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                      setValue("vehicleId", "");
                      setValue("merchant", "");
                      setSelectedVehicleOdometer(0);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Accounts..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("vehicleId", "");
                      setValue("merchant", "");
                      setSelectedVehicleOdometer(0);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="vehicleId"
              control={control}
              rules={{ required: "Please select a vehicle." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Vehicle"
                  required
                  value={
                    vehicles?.find((vec) => vec._id === value?._id) ??
                    value ??
                    null
                  }
                  onChange={(_, data) => {
                    onChange(data);
                    // Reset odometer field when vehicle changes
                    setValue("odometer", "");
                  }}
                  getOptionLabel={(opt) =>
                    opt ? `${opt.vehicle_id} - ${opt.registration_number}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={vehicles || []}
                  loading={loadingVehicles}
                  loadingText="Loading Vehicles..."
                  helperText={errors?.vehicleId?.message}
                  error={!!errors?.vehicleId}
                  disabled={!!vehicleDetail}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="merchant"
              control={control}
              rules={{ required: "Please select a fuel station." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Fuel Station"
                  required
                  value={
                    merchants?.find((m) => m._id === value?._id) ??
                    value ??
                    null
                  }
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(opt) =>
                    opt ? `${opt.merchantName} (${opt.merchantCode})` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={merchants || []}
                  loading={loadingMerchants}
                  loadingText="Loading Fuel Stations..."
                  helperText={errors?.merchant?.message}
                  error={!!errors?.merchant}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="odometer"
              control={control}
              rules={{
                required: "Odometer is required",
                min: {
                  value: 0,
                  message: "Odometer cannot be negative",
                },
                // validate: (value) => {
                //   const numValue = Number(value);
                //   if (selectedVehicleOdometer && numValue <= selectedVehicleOdometer) {
                //     return `Odometer must be greater than previous reading (${selectedVehicleOdometer})`;
                //   }
                //   return true;
                // }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Odometer"
                  type="number"
                  inputProps={{ min: selectedVehicleOdometer + 1 }}
                  error={!!errors.odometer}
                  helperText={
                    errors?.odometer?.message ||
                    (selectedVehicleOdometer > 0
                      ? `Previous odometer: ${selectedVehicleOdometer}`
                      : "")
                  }
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="fuelDate"
              control={control}
              rules={{ required: "Fuel Date is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Fuel Date"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  helperText={errors?.fuelDate?.message}
                  error={!!errors?.fuelDate}
                />
              )}
            />
          </Grid>

          {/* Fuel Details */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showFuelDetails}
                  onChange={(e) => setShowFuelDetails(e.target.checked)}
                  icon={<CheckBoxOutlineBlankOutlined />}
                  checkedIcon={<CheckBoxOutlined />}
                />
              }
              label="Add Fuel Details"
            />
          </Grid>
          {showFuelDetails && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {fields.map((field, index) => (
                  <Grid
                    key={field.id}
                    container
                    item
                    xs={12}
                    spacing={4}
                    alignItems="center"
                  >
                    <Grid item xs={4}>
                      <Controller
                        name={`fuel.${index}.fuelType`}
                        control={control}
                        rules={{ required: "Fuel Type is required" }}
                        render={({ field }) => (
                          <BaseSelect
                            {...field}
                            required
                            fullWidth
                            size="small"
                            label="Fuel Type"
                            options={FUEL_TYPES}
                            helperText={
                              errors?.fuel?.[index]?.fuelType?.message
                            }
                            error={!!errors?.fuel?.[index]?.fuelType}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Controller
                        name={`fuel.${index}.price`}
                        control={control}
                        rules={{
                          required: "Price is required",
                          validate: (value) =>
                            value > 0 || "Price must be greater than 0",
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            fullWidth
                            size="small"
                            label="Price per Unit"
                            type="number"
                            error={!!errors?.fuel?.[index]?.price}
                            helperText={errors?.fuel?.[index]?.price?.message}
                            inputProps={{ min: 0 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Controller
                        name={`fuel.${index}.quantity`}
                        control={control}
                        rules={{
                          required: "Quantity is required",
                          validate: (value) =>
                            value > 0 || "Quantity must be greater than 0",
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            fullWidth
                            size="small"
                            label="Quantity"
                            type="number"
                            error={!!errors?.fuel?.[index]?.quantity}
                            inputProps={{ min: 0 }}
                            helperText={
                              errors?.fuel?.[index]?.quantity?.message
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={2}
                      display="flex"
                      justifyContent="flex-end"
                      gap={2}
                      alignItems="center"
                      sx={{ paddingBottom: 1 }}
                    >
                      <IconButton
                        aria-label="delete"
                        color="error"
                        onClick={() => remove(index)}
                        sx={{
                          height: "35px",
                          width: "35px",
                          "&:hover": {
                            backgroundColor: "rgba(255, 0, 0, 0.2)",
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                      <IconButton
                        aria-label="add"
                        color="primary"
                        onClick={() =>
                          append({ fuelType: "", price: "", quantity: "" })
                        }
                        sx={{
                          height: "35px",
                          width: "35px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 123, 255, 0.2)",
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <Add />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
          <Grid item md={12}>
            <FormHelperText>Attachments</FormHelperText>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value ?? []}
                  onFileChange={(files) => setValue("attachments", files)}
                />
              )}
            />
          </Grid>
          {vehicleFuelDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnVehicleFuelSubmit)}
        >
          {vehicleFuelDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default VehicleFuelForm;
